import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import { BrandgenListDomain } from "../../../../types/campaignTypes";

type DomainTagListProps = {
  domains: BrandgenListDomain[];
  isSelectable?: boolean;
  selectedDomainIds?: string[];
  onDomainSelect?: (domainId: string) => void;
};

const TagsWrapper = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  padding: "10px 16px 12px",
});

// Display-only tag (management / non-split mode)
const Tag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "default",
}));

// Selectable, unselected state (split mode)
const SelectableTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  transition: "color 0.15s, background 0.15s, border-color 0.15s",
  userSelect: "none",
  "&:hover": {
    color: theme.palette.brand.white,
    background: theme.palette.brand.coral,
    borderColor: theme.palette.brand.coral,
  },
}));

// Selectable, selected state (split mode, active selection)
const SelectedTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.brand.white,
  background: theme.palette.brand.coral,
  border: `1px solid ${theme.palette.brand.coral}`,
  cursor: "pointer",
  transition: "color 0.15s, background 0.15s, border-color 0.15s",
  userSelect: "none",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    borderColor: theme.palette.brand.coralHover,
  },
}));

const EmptyLabel = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "block",
  padding: "10px 16px",
  fontSize: 12,
  color: theme.palette.brand.gray400,
}));

const DomainTagList: FC<DomainTagListProps> = ({ domains, isSelectable, selectedDomainIds = [], onDomainSelect }) => {
  const active = domains.filter((d) => d.active);

  if (active.length === 0) return <EmptyLabel>No active domains in this list.</EmptyLabel>;

  return (
    <TagsWrapper>
      {active.map((d) => {
        if (!isSelectable || !onDomainSelect) {
          return <Tag key={d.id}>{d.name}</Tag>;
        }
        const isSelected = selectedDomainIds.includes(d.id);
        const TagComponent = isSelected ? SelectedTag : SelectableTag;
        return (
          <TagComponent
            key={d.id}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onDomainSelect(d.id);
            }}
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && onDomainSelect(d.id)}
          >
            {d.name}
          </TagComponent>
        );
      })}
    </TagsWrapper>
  );
};

export default DomainTagList;
